import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import PageTitle from "./PageTitleCompoent";
import "../css/AddProductScreen.css";
import component96 from "../assets/Component96.png";
import api from "../api";

export default function AddNewShop() {
  const navigate = useNavigate();
  const location = useLocation();
  const { shopId, productId } = location.state || {};

  const defaultProfileImg = component96;
  const [profileImg, setProfileImg] = useState(defaultProfileImg);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const imageInputRef = useRef(null);

  const [formData, setFormData] = useState({
    productName: "",
    availableStocks: "",
    description: "",
    subCategory: "",
    originalPrice: "",
    realPrice: "",
    caption: "",
    category: "",
    weight: "", // ✅ added
    weightUnit: "g", // ✅ added
  });

  const [promoTag, setPromoTag] = useState("");
  const tagMapping = {
    Popular: "popular",
    "Steal Deals": "steal_deal",
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handleBack = () => navigate(-1);

  useEffect(() => {
    if (!shopId) {
      alert("Shop ID missing. Please go back and select a shop again.");
      navigate(-1);
    }
  }, [shopId, navigate]);

  useEffect(() => {
    const fetchProductDetails = async () => {
      if (!shopId || !productId) return;
      try {
        setLoading(true);
        setError("");

        const token = localStorage.getItem("accessToken");
        const response = await api.get(
          `/admin/shops/${shopId}/detail?product_id=${productId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const productInfo = response.data?.products?.[0];
        if (productInfo) {
          setFormData({
            productName: productInfo.name || "",
            availableStocks: productInfo.quantity || "",
            description: productInfo.description || "",
            subCategory: productInfo.product_category || "",
            originalPrice: productInfo.original_price || "",
            realPrice: productInfo.actual_price || "",
            caption: productInfo.caption || "",
            category: productInfo.pet_category || "",
            weight: productInfo.weight || "", // ✅ added
            weightUnit: productInfo.weight_unit || "g", // ✅ added
          });

          if (productInfo.tags) {
            const foundTag = Object.keys(tagMapping).find(
              (key) => tagMapping[key] === productInfo.tags
            );
            if (foundTag) setPromoTag(foundTag);
          }

          if (productInfo.image) {
            const imageUrl = productInfo.image.startsWith("http")
              ? productInfo.image
              : `${api.defaults.baseURL.split("/api")[0]}${productInfo.image}`;
            setProfileImg(imageUrl);
          }
        } else {
          setError("Product not found.");
        }
      } catch (err) {
        console.error("❌ Failed to fetch product details:", err);
        setError("Failed to fetch product details. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchProductDetails();
  }, [shopId, productId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const calculateDiscount = () => {
    const original = parseFloat(formData.originalPrice);
    const real = parseFloat(formData.realPrice);
    if (!isNaN(original) && !isNaN(real) && original > real) {
      return Math.round(((original - real) / original) * 100);
    }
    return 0;
  };

  const handleSaveProduct = async () => {
    try {
      setLoading(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("accessToken");
      if (!token) {
        setError("Access token missing. Please login again.");
        setLoading(false);
        return;
      }

      const discountPercentage = calculateDiscount();

      const formDataToSend = new FormData();
      formDataToSend.append("name", formData.productName);
      formDataToSend.append("quantity", formData.availableStocks);
      formDataToSend.append("description", formData.description);
      formDataToSend.append("product_category", formData.subCategory);
      formDataToSend.append("original_price", formData.originalPrice);
      formDataToSend.append("actual_price", formData.realPrice);
      formDataToSend.append("discount_percentage", discountPercentage);
      formDataToSend.append("caption", formData.caption);
      formDataToSend.append("pet_category", formData.category);
      formDataToSend.append("tags", tagMapping[promoTag] || "");
      formDataToSend.append("weight", formData.weight); // ✅ added
      formDataToSend.append("weight_unit", formData.weightUnit); // ✅ added

      if (selectedImageFile) {
        formDataToSend.append("image", selectedImageFile);
      }

      let response;
      if (productId) {
        response = await api.put(`/products/${productId}/`, formDataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSuccess("Product updated successfully!");
      } else {
        response = await api.post(
          `/shops/${shopId}/products/add/`,
          formDataToSend,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSuccess("Product added successfully!");
      }

      console.log("✅ Product Saved:", response.data);

      setFormData({
        productName: "",
        availableStocks: "",
        description: "",
        subCategory: "",
        originalPrice: "",
        realPrice: "",
        caption: "",
        category: "",
        weight: "",
        weightUnit: "g",
      });
      setPromoTag("");
      setSelectedImageFile(null);
      setProfileImg(defaultProfileImg);

      setTimeout(() => navigate(-1), 1000);
    } catch (err) {
      console.error("❌ Failed to save product:", err);
      setError("Failed to save product. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <p>Loading product data...</p>
      </div>
    );
  }

  return (
    <div className="add-newshop-container">
      <Sidebar />
      <div className="add-newshop-main">
        <Header />
        <div className="add-newshop-content">
          <div className="add-newshop-heading-row">
            <PageTitle
              title={productId ? "Edit Product" : "Add Product"}
              onBack={handleBack}
            />
          </div>

          <div className="add-newshop-card">
            <form className="add-newshop-newshop-form">
              <h3 className="add-newshop-label1">
                {productId ? "Edit Product" : "Add New Product"}
              </h3>

              <div className="add-newshop-form-main">
                {/* ===== Row 1 ===== */}
                <div className="add-newshop-form-row1">
                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Product Name</label>
                    <input
                      type="text"
                      name="productName"
                      className="add-newshop-input"
                      value={formData.productName}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Discount %</label>
                    <input
                      type="text"
                      name="discount"
                      className="add-newshop-input"
                      value={calculateDiscount()}
                      readOnly
                    />
                  </div>

                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Available Stocks</label>
                    <input
                      type="text"
                      name="availableStocks"
                      className="add-newshop-input"
                      value={formData.availableStocks}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Description</label>
                    <input
                      type="text"
                      name="description"
                      className="add-newshop-input"
                      value={formData.description}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Sub Category</label>
                    <select
                      className="add-newshop-input1"
                      name="subCategory"
                      value={formData.subCategory}
                      onChange={handleInputChange}
                    >
                      <option value="food">Food</option>
                      <option value="accessories">Accessories</option>
                      <option value="grooming">Grooming</option>
                      <option value="treats">Treats</option>
                      <option value="toys">Toys</option>
                      <option value="litter">Litter</option>
                      <option value="walk">Walk</option>
                      <option value="bowls">Bowls</option>
                      <option value="supplements">Supplements</option>
                      <option value="monsoon">Monsoon</option>
                    </select>
                  </div>

                  {/* ✅ Promo Tags */}
                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Tags</label>
                    <div className="Add-package-tags-buttons">
                      {["Popular", "Steal Deals"].map((tagOption) => (
                        <button
                          key={tagOption}
                          className={`Add-package-tag-button ${
                            promoTag === tagOption
                              ? `${tagMapping[tagOption]}_selected`
                              : ""
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            setPromoTag(tagOption);
                          }}
                          type="button"
                        >
                          {tagOption}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* ===== Row 2 ===== */}
                <div className="add-newshop-form-row1">
                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Original Price</label>
                    <input
                      type="text"
                      name="originalPrice"
                      className="add-newshop-input"
                      value={formData.originalPrice}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Real Price</label>
                    <input
                      type="text"
                      name="realPrice"
                      className="add-newshop-input"
                      value={formData.realPrice}
                      onChange={handleInputChange}
                    />
                  </div>

                  {/* ✅ New Weight Input */}
                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Weight</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <input
                        type="number"
                        name="weight"
                        className="add-newshop-input"
                        value={formData.weight}
                        onChange={handleInputChange}
                        placeholder="Enter weight"
                        style={{ flex: "1" }}
                      />
                      <select
                        name="weightUnit"
                        value={formData.weightUnit}
                        onChange={handleInputChange}
                        className="add-newshop-input1"
                        style={{ width: "80px" }}
                      >
                        <option value="g">g</option>
                        <option value="kg">kg</option>
                      </select>
                    </div>
                  </div>

                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Caption</label>
                    <input
                      type="text"
                      name="caption"
                      className="add-newshop-input"
                      value={formData.caption}
                      onChange={handleInputChange}
                    />
                  </div>

                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Category</label>
                    <select
                      className="add-newshop-input1"
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Category</option>
                      <option value="dog">Dog</option>
                      <option value="cat">Cat</option>
                    </select>
                  </div>

                  <div className="add-newshop-form-group">
                    <label className="add-newshop-label">Product Image</label>
                    <img
                      src={profileImg}
                      alt="Preview"
                      style={{
                        width: "60.64px",
                        height: "60.12px",
                        cursor: "pointer",
                      }}
                      onClick={() => imageInputRef.current.click()}
                    />
                    <input
                      type="file"
                      ref={imageInputRef}
                      style={{ display: "none" }}
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files[0];
                        if (file) {
                          setSelectedImageFile(file);
                          setProfileImg(URL.createObjectURL(file));
                        }
                      }}
                    />
                  </div>
                </div>
              </div>

              <div className="add-newshop-save-button-wrapper">
                <button
                  className="add-newshop-Save-btn"
                  type="button"
                  onClick={handleSaveProduct}
                  disabled={loading}
                >
                  <span className="add-newshop-Save-icon">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                    >
                      <path
                        fill="none"
                        stroke="#fff"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="1.5"
                        d="M12 4.5v15m7.5-7.5h-15"
                      />
                    </svg>
                  </span>
                  <span className="add-newshop-Save-text">
                    {loading
                      ? productId
                        ? "Updating..."
                        : "Adding..."
                      : productId
                      ? "Update Product"
                      : "Add Product"}
                  </span>
                </button>
              </div>

              {success && <p className="success-message">{success}</p>}
              {error && <p className="error-message">{error}</p>}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
